import styled, { css } from "styled-components";

import {
    FRAME_HOVER_BACKGROUND_COLOR,
    onBackgroundImage,
} from "components/common/backgroundFrame";
import { CSS_VARIABLES } from "enums/cssVariables";

import { BaseOptionButton } from "./BaseOptionButton";

type Props = {
    text: string;
    isActive: boolean;
    isDisabled: boolean;
    onClick: () => void;
    color?: string;
    image?: string;
    description?: string;
};

const IS_ACTIVE_BG = "#60b860";

const BasicCaseProposal = ({
    text,
    isActive,
    isDisabled,
    onClick,
    color,
    image,
    description,
}: Props) => {
    const hasImage = !!image;
    const imgSrc = hasImage ? `${image}` : undefined;
    const hasAdditionalContent = !!(hasImage || description);

    return (
        <StyledButton
            disabled={isDisabled}
            onClick={onClick}
            $isActive={isActive}
            $color={color}
            $hasElectionContent={hasAdditionalContent}
            aria-label={`${text}-vote-basic`}
            data-testactive={isActive}
        >
            {hasAdditionalContent ? (
                <ContentWrapper>
                    {hasImage && (
                        <ProposalImage src={imgSrc} alt={`Image for ${text}`} />
                    )}
                    <TextWrapper>
                        <StyledText $hasElectionContent={hasAdditionalContent}>
                            {text}
                        </StyledText>
                        {description && (
                            <DescriptionText $isActive={isActive}>
                                {description}
                            </DescriptionText>
                        )}
                    </TextWrapper>
                </ContentWrapper>
            ) : (
                <StyledText $hasElectionContent={hasAdditionalContent}>
                    {text}
                </StyledText>
            )}
        </StyledButton>
    );
};

const ContentWrapper = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    text-align: left;
`;

const ProposalImage = styled.img`
    width: 50px;
    height: 50px;
    object-fit: cover;
    margin-right: 10px;
    border-radius: 4px;
`;

const TextWrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    word-break: break-word;
    white-space: normal;
    max-width: 100%;
`;

interface StyledButtonProps {
    $isActive: boolean;
    $hasElectionContent?: boolean;
    $color?: string;
}
const StyledButton = styled(BaseOptionButton)<StyledButtonProps>`
    background: ${(props) =>
        props.$isActive
            ? props.$color || IS_ACTIVE_BG
            : `var(${CSS_VARIABLES.UNSELECTED_OPTION_BACKGROUND_COLOR}, transparent)`};
    color: ${(props) =>
        props.$isActive ? "#ffffff " : `${props.$color || "black"} `};
    border: ${(props) => `1px solid ${props.$color || "#cccccc"}`};
    border-radius: 4px;
    justify-content: ${(props) =>
        props.$hasElectionContent ? "flex-start" : "center"};
    padding: 8px 12px;
    min-height: 40px;
    align-items: center;
    &:hover:not([disabled]) {
        background: ${(props) =>
            props.$isActive
                ? props.$color || IS_ACTIVE_BG
                : `${props.$color || "#f0f0f0"}`};
        color: ${(props) =>
            props.$isActive ? "#ffffff" : props.$color ? "#ffffff" : "black"};
    }
    /* Uncolored options (election candidates): the hover, sticky on mobile after a tap, must not turn white in the frame. */
    ${(props) =>
        !props.$isActive &&
        !props.$color &&
        onBackgroundImage(css`
            &:hover:not([disabled]) {
                background: ${FRAME_HOVER_BACKGROUND_COLOR};
            }
        `)}
    &[disabled] {
        background: ${(props) =>
            props.$isActive ? props.$color || "#60b860" : "#e8e8e8"};
        opacity: 0.6;
        cursor: not-allowed;
        border-color: ${(props) =>
            props.$isActive ? props.$color : "#e0e0e0"};
        color: ${(props) => (props.$isActive ? "#ffffff" : "#a0a0a0")};
    }
`;

const StyledText = styled.p<{ $hasElectionContent?: boolean }>`
    margin: ${(props) => (props.$hasElectionContent ? "0" : "auto")};
    font-weight: ${(props) => (props.$hasElectionContent ? "bold" : "")};
`;

const DescriptionText = styled.p<{ $isActive?: boolean }>`
    margin: 0;
    font-size: 0.9em;
    color: ${(props) => (props.$isActive ? "#ffffff" : "#555555")};
`;
export default BasicCaseProposal;
